import classNames from 'classnames';

import {
  Button,
  cssVarToIconColorProp,
  Icon,
  LinkButton,
} from '@iconik-io/uad';

import TooltipTrigger from '../tooltip_trigger/tooltip_trigger';
import Badge from './badge';
import type { SidebarMenuItemType } from './sidebar_menu';

import styles from './sidebar_menu_button.module.css';

type Props = {
  item: SidebarMenuItemType;
  isParentItem?: boolean;
  isExpanded?: boolean;
  onItemClick?: (item: SidebarMenuItemType) => void;
};

function ButtonContents({ item, isParentItem, isExpanded }: Props) {
  const itemColor = item.disabled
    ? 'primary_neutral'
    : cssVarToIconColorProp(item.color);

  return (
    <>
      <div className={styles.icon_wrapper}>
        <Icon name={item.iconName} color={itemColor} />
        <Badge
          badge={item.badge}
          color={item.badgeColor ?? item.color}
          aria={{
            label: item.badgeLabel,
          }}
        />
      </div>

      {isExpanded ? (
        <span className={styles.button_title}>
          {item.title}{' '}
          {isParentItem ? (
            <Icon
              className={styles.arrow}
              size="xs"
              name={
                item.active
                  ? 'chevron_small_isolated_s'
                  : 'chevron_small_isolated_e'
              }
            />
          ) : null}
        </span>
      ) : null}
    </>
  );
}

export default function SidebarMenuButton({
  item,
  isParentItem,
  isExpanded,
  onItemClick,
}: Props) {
  const disableTooltip = item.disabledTooltipText
    ? false
    : Boolean(item.disabled) || isExpanded;

  return (
    <TooltipTrigger
      key={item.iconName}
      placement="right"
      tooltipText={item.disabledTooltipText ?? item.title}
      tooltipClass={styles.tooltip}
      disabled={disableTooltip}
    >
      {item.disabled || onItemClick ? (
        <Button
          className={classNames(styles.button, {
            [styles.grouped]: isParentItem || item.parent,
            [styles.active]: item.active && !item.disabled,
            [styles.button_expanded]: isExpanded,
          })}
          isDisabled={Boolean(item.disabled)}
          onClick={() => onItemClick?.(item)}
          aria-label={item.title}
          aria-pressed={item.active}
          variant="tertiary"
        >
          <ButtonContents
            item={item}
            isParentItem={isParentItem}
            isExpanded={isExpanded}
          ></ButtonContents>
        </Button>
      ) : (
        <LinkButton
          href={item.key}
          className={classNames(styles.button, {
            [styles.grouped]: isParentItem || item.parent,
            [styles.active]: item.active && !item.disabled,
            [styles.button_expanded]: isExpanded,
          })}
          aria-label={item.title}
          aria-pressed={item.active}
          variant="tertiary"
        >
          <ButtonContents
            item={item}
            isParentItem={isParentItem}
            isExpanded={isExpanded}
          ></ButtonContents>
        </LinkButton>
      )}
    </TooltipTrigger>
  );
}
